// Cell formatting for the object list pages. Values come from the API as JSON,
// so each formatter accepts unknown and renders '' for anything missing.

const TIME_ZONE = 'America/New_York';

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

/** `closed_won` → "Closed won"; anything that is not a string is shown as is. */
export function humanize(value: unknown): string {
  if (isBlank(value)) return '';
  const text = String(value).replace(/[_-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A calendar date ("2026-10-14" or an ISO timestamp) as "Oct 14, 2026". */
export function formatDate(value: unknown): string {
  if (isBlank(value)) return '';
  const text = String(value);
  // A bare date has no time zone; read it as that calendar day, not UTC midnight.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T12:00:00Z`) : new Date(text);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: /^\d{4}-\d{2}-\d{2}$/.test(text) ? 'UTC' : TIME_ZONE,
  });
}

/** An ISO timestamp as "Oct 14" in Olytic's time zone, with the year when it is not this year's. */
export function formatShortDate(value: unknown, now: Date = new Date()): string {
  if (isBlank(value)) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const year = (d: Date) => d.toLocaleDateString('en-US', { year: 'numeric', timeZone: TIME_ZONE });
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(year(date) === year(now) ? {} : { year: 'numeric' }),
    timeZone: TIME_ZONE,
  });
}

/** An ISO timestamp as "Oct 14, 2026, 3:05 PM" in Olytic's time zone. */
export function formatDateTime(value: unknown): string {
  if (isBlank(value)) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
}

const toNumber = (value: unknown): number | null => {
  if (isBlank(value)) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Whole dollars, e.g. "$42,000". Postgres numerics may arrive as strings. */
export function formatCurrency(value: unknown): string {
  const n = toNumber(value);
  return n === null ? '' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

/** Dollars and cents, e.g. "$42,000.00": billing amounts, which are invoiced to the cent. */
export function formatMoney(value: unknown): string {
  const n = toNumber(value);
  return n === null
    ? ''
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatNumber(value: unknown, maximumFractionDigits = 2): string {
  const n = toNumber(value);
  return n === null ? '' : n.toLocaleString('en-US', { maximumFractionDigits });
}

export function formatPercent(value: unknown): string {
  const n = toNumber(value);
  return n === null ? '' : `${formatNumber(n, 0)}%`;
}

export function formatBytes(value: unknown): string {
  const n = toNumber(value);
  if (n === null) return '';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = n / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toLocaleString('en-US', { maximumFractionDigits: size < 10 ? 1 : 0 })} ${units[unit]}`;
}

export function formatYesNo(value: unknown): string {
  if (isBlank(value)) return '';
  return value === true || value === 'true' ? 'Yes' : 'No';
}

/** "Oct 2026" for a month, YYYY-MM or YYYY-MM-DD; '' for anything else. */
export function formatMonthYear(value: unknown): string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}(-\d{2})?$/.test(value)) return '';
  return new Date(`${value.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
